import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Format provider name from first/last name, with fallback to business name
 * Returns format like "John D." for better SMS readability
 */
export function formatProviderName(
  firstName?: string, 
  lastName?: string, 
  businessName?: string
): string {
  if (firstName && lastName) {
    return `${firstName} ${lastName.charAt(0)}.`;
  }
  if (firstName) {
    return firstName;
  }
  if (businessName) {
    return businessName;
  }
  return 'Your provider';
}

export function useClientNotifications() {
  const [isSending, setIsSending] = useState(false);

  const sendMessage = async ({
    clientId,
    phone,
    message,
    type
  }: {
    clientId: string;
    phone: string;
    message: string;
    type: 'booking_confirmation' | 'reminder' | 'rebooking_nudge';
  }) => {
    try {
      setIsSending(true);

      // Check client consent first
      const { data: consentRow } = await supabase
        .from('clients')
        .select('sms_consent')
        .eq('id', clientId)
        .maybeSingle();

      if (consentRow && consentRow.sms_consent === false) {
        toast.error('Cannot send SMS: client has not consented.');
        return false;
      }

      // First, create the message record
      const { data: messageData, error: dbError } = await supabase
        .from('client_messages')
        .insert({
          client_id: clientId,
          message_type: type,
          content: message
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Then send it via the edge function
      const { error: sendError } = await supabase.functions.invoke('send-notifications', {
        body: {
          message_id: messageData.id,
          phone,
          message,
          type
        }
      });

      if (sendError) throw sendError;

      toast.success('Message sent successfully');
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      return false;
    } finally {
      setIsSending(false);
    }
  };

  const sendRebookingReminder = async (clientId: string, phone: string, lastVisit: Date) => {
    const message = `We miss you! Your last visit was on ${new Date(lastVisit).toLocaleDateString()}. Ready for another appointment?`;
    return sendMessage({
      clientId,
      phone,
      message,
      type: 'rebooking_nudge'
    });
  };

  const sendAppointmentReminder = async (
    clientId: string,
    phone: string,
    appointmentDate: Date,
    providerName?: string,
    businessTimezone?: string,
    bookingId?: string
  ) => {
    // A booking ID is required — the SMS must carry a per-booking /r/{slug}
    // link. No fallback to the generic /manage URL.
    if (!bookingId) {
      console.error('sendAppointmentReminder called without bookingId — refusing to send generic link');
      toast.error('Cannot send reminder: booking ID missing.');
      return false;
    }

    let manageUrl: string;
    try {
      const { data: linkData, error: linkError } = await supabase.rpc('generate_booking_link', {
        booking_id: bookingId,
        ttl_minutes: 43200 // 30 days
      });
      if (linkError || !linkData?.slug) {
        throw new Error(linkError?.message ?? 'generate_booking_link returned no slug');
      }
      manageUrl = `https://getfiggy.link/r/${linkData.slug}`;
    } catch (error) {
      console.error('Failed to generate secure booking link for reminder:', error);
      toast.error('Cannot send reminder: booking link could not be generated.');
      return false;
    }

    // Fetch timezone and provider info if bookingId provided
    let resolvedTimezone = businessTimezone;
    let resolvedProviderName = providerName;
    
    if (bookingId && (!businessTimezone || !providerName)) {
      try {
        const { data: bookingInfo } = await supabase
          .from('bookings')
          .select(`
            business_profiles(name, timezone),
            providers(first_name, last_name)
          `)
          .eq('id', bookingId)
          .single();
          
        if (bookingInfo) {
          resolvedTimezone = resolvedTimezone || bookingInfo.business_profiles?.timezone;
          if (!resolvedProviderName && bookingInfo.providers) {
            resolvedProviderName = formatProviderName(
              bookingInfo.providers.first_name,
              bookingInfo.providers.last_name,
              bookingInfo.business_profiles?.name
            );
          } else if (!resolvedProviderName) {
            resolvedProviderName = bookingInfo.business_profiles?.name || 'Your provider';
          }
        }
      } catch (error) {
        console.warn('Failed to fetch booking timezone/provider info:', error);
      }
    }
    
    // Format date in the business's timezone (fallback to system timezone)
    const timezone = resolvedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const finalProviderName = resolvedProviderName || 'Your provider';
    
    try {
      // Use timezone-aware formatting
      const dayName = appointmentDate.toLocaleDateString('en-US', { 
        weekday: 'long',
        timeZone: timezone
      });
      const monthDay = appointmentDate.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric',
        timeZone: timezone
      });
      const time = appointmentDate.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true,
        timeZone: timezone
      });

      const message = `Get ready! Your appointment with ${finalProviderName} is coming up on ${dayName}, ${monthDay} at ${time}. Manage your booking here: ${manageUrl}`;
      return sendMessage({
        clientId,
        phone,
        message,
        type: 'reminder'
      });
    } catch (timezoneError) {
      console.warn('Timezone formatting failed, using system timezone:', timezoneError);
      
      // Fallback to system timezone
      const dayName = appointmentDate.toLocaleDateString('en-US', { weekday: 'long' });
      const monthDay = appointmentDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
      const time = appointmentDate.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });

      const message = `Get ready! Your appointment with ${finalProviderName} is coming up on ${dayName}, ${monthDay} at ${time}. Manage your booking here: ${manageUrl}`;
      return sendMessage({
        clientId,
        phone,
        message,
        type: 'reminder'
      });
    }
  };

  const sendBookingConfirmation = async (clientId: string, phone: string, appointmentDate: Date, serviceName: string, bookingId?: string) => {
    // A booking ID is required — the SMS must carry a per-booking /r/{slug}
    // link. No fallback to the generic /manage URL.
    if (!bookingId) {
      console.error('sendBookingConfirmation called without bookingId — refusing to send generic link');
      toast.error('Cannot send confirmation: booking ID missing.');
      return false;
    }

    let manageUrl: string;
    try {
      const { data: linkData, error: linkError } = await supabase.rpc('generate_booking_link', {
        booking_id: bookingId,
        ttl_minutes: 43200 // 30 days
      });
      if (linkError || !linkData?.slug) {
        throw new Error(linkError?.message ?? 'generate_booking_link returned no slug');
      }
      manageUrl = `https://getfiggy.link/r/${linkData.slug}`;
    } catch (error) {
      console.error('Failed to generate secure booking link for confirmation:', error);
      toast.error('Cannot send confirmation: booking link could not be generated.');
      return false;
    }

    const message = `Thank you for booking your ${serviceName} appointment with us for ${appointmentDate.toLocaleString()}. We look forward to seeing you! Need to make changes? Visit: ${manageUrl}`;
    return sendMessage({
      clientId,
      phone,
      message,
      type: 'booking_confirmation'
    });
  };

  return {
    sendMessage,
    sendRebookingReminder,
    sendAppointmentReminder,
    sendBookingConfirmation,
    isSending
  };
}
